import React, { useEffect, useState } from 'react';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import {
  App, Button, Form, UploadFile,
} from 'antd';
import Tabs from 'antd/es/tabs';
import SinglePageContainer from '../../../Common/SinglePageContainer';
import { useMessageError } from '../../../../hooks/common';
import ComplexesFormItemsGeneral from '../Form/General';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { generateFileObjFromUrl } from '../../../Common/Uploaders/ImageUpload';
import { asyncDelay, filterObjectProps, isObjEqual } from '../../../../utils';
import { useUnsavedChanges } from '../../../../context/unsavedChanges';
import {
  Complex, ComplexWeek, CreateComplexPayload, useComplexDelete, useComplexId, useComplexUpdate,
} from '../../../../hooks/api/complexes';
import ComplexesFormItemsTrainings, { initialWeekState } from '../Form/Trainings';

const breadcrumbs: ItemType[] = [
  {
    title: <NavLink to="/complexes">Комплексы</NavLink>,
  },
  {
    title: 'Редактировать комплекс',
  },
];

const initialState: Partial<CreateComplexPayload> = {
  nameRU: '',
  nameUA: '',
  descriptionRU: '',
  descriptionUA: '',
  availability: undefined,
  contentState: 'draft',
  level: undefined,
  imageUrl: '',
  durationInWeeks: 1,
  weeks: [initialWeekState],
};

const keysToFilterInitial: (keyof Partial<Complex>)[] = ['createdAt', 'updatedAt', 'id'];

export default function ComplexUpdate() {
  const { id = '' } = useParams<{ id: string; }>();
  const complexById = useComplexId();
  const complexUpdate = useComplexUpdate();
  const complexDelete = useComplexDelete();
  const navigate = useNavigate();

  const { open, contextHolder } = useSimpleModal();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const {
    unsavedChanges, handleUnsavedChanges, htmlEditorTouched, handleEditorTouched,
  } = useUnsavedChanges();

  const fetch = () => {
    complexById.fetch(undefined, id);
  };

  useEffect(() => {
    if (id) {
      fetch();
    }
  }, [id]);

  const [bannerFile, setBannerFile] = useState<UploadFile[]>([]);

  /** Handle Initial values */
  const [initialValues, setInitialValues] = useState(initialState);

  const handleInitialFileList = (data: Complex) => {
    if (data?.imageUrl) {
      const banner = generateFileObjFromUrl(data.imageUrl);

      setBannerFile([banner]);
    }
  };

  useEffect(() => {
    if (complexById.data) {
      setInitialValues(filterObjectProps(complexById.data, keysToFilterInitial));
      handleInitialFileList(complexById.data);
    }
  }, [complexById.data]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    handleUnsavedChanges(false);
    asyncDelay(200).then(() => (handleEditorTouched(false)));
  }, [initialValues]);

  /** Submit */
  const handleSubmitClick = () => {
    form.validateFields().catch(() => { setWithErrors(true); });
    form.submit();
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const modifiedVal = filterObjectProps(values, ['weeks']);

      /** If weeks changed, then add they to payload */
      if (!isObjEqual(initialValues.weeks, values.weeks)) {
        modifiedVal.weeks = (values.weeks || []).map((week: ComplexWeek) => ({
          id: week.id,
          trainingDays: week.trainingDays.map((trainingDay) => ({
            id: trainingDay.id,
            trainings: trainingDay.trainings.map((training) => ({
              id: training.id,
            })),
          })),
        }));
      }

      return (
        complexUpdate.fetch(modifiedVal, id)
          .then((res) => {
            if (res?.id) {
              message.success('Изменения успешно сохранены.');
              setInitialValues(filterObjectProps(res, keysToFilterInitial));
            }
          })
      );
    });
  };

  /** Handle Delete */
  const handleDeleteClick = () => {
    if (!id) return;
    open({
      title: 'Удалить комплекс?',
      content: 'Вы уверены, что хотите навсегда удалить этот комплекс?',
      cancelText: 'Отмена',
      centered: true,
      okText: 'Удалить',
      okButtonProps: {
        type: 'primary',
        danger: true,
      },
      onOk: () => complexDelete.fetch(id),
      maskClosable: true,
    });
  };

  useEffect(() => {
    if (complexDelete.response?.status === 204) {
      handleUnsavedChanges(false).then(() => {
        message.success('Комплекс успешно удален.');
        navigate('/complexes');
      });
    }
  }, [complexDelete.response]);

  const weeksWatch = Form.useWatch('weeks', form);
  const [withErrors, setWithErrors] = useState(false);

  const handleFieldsChange = () => {
    setWithErrors(form.getFieldsError().some((field) => field.errors.length));
    handleUnsavedChanges(!isObjEqual(
      filterObjectProps(initialValues, ['descriptionRU', 'descriptionUA']),
      filterObjectProps(form.getFieldsValue(), ['descriptionRU', 'descriptionUA']),
    ));
  };

  useEffect(() => { handleFieldsChange(); }, [bannerFile, weeksWatch]);

  useMessageError([complexById, complexUpdate, complexDelete]);

  return (
    <>
      {contextHolder}
      <SinglePageContainer
        breadcrumbItems={breadcrumbs}
        title="Редактировать комплекс"
        cardLoading={complexById.loading}
        extra={(
          <>
            <Button type="primary" danger onClick={handleDeleteClick}>Удалить</Button>
            <Button
              type="primary"
              onClick={handleSubmitClick}
              loading={complexUpdate.loading}
              disabled={!(unsavedChanges || htmlEditorTouched)}
            >
              Сохранить
            </Button>
          </>
      )}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSubmit}
          name="complex-update"
          onFieldsChange={handleFieldsChange}
        >
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: <span className={withErrors ? 'with-error' : undefined}>Общее</span>,
                children: (
                  <ComplexesFormItemsGeneral
                    bannerFile={bannerFile}
                    setBannerFile={setBannerFile}
                  />
                ),
              },
              {
                key: '2',
                label: 'Тренировки',
                children: (
                  <ComplexesFormItemsTrainings />
                ),
              },
            ]}
          />
        </Form>
      </SinglePageContainer>
    </>
  );
}
